import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, UserPlus, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import LevelAvatar from '../components/engagement/LevelAvatar';
import { fetchInvite, acceptInvite } from '../lib/meApi';
import { toast } from 'sonner';

const InvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const isLogged = !!localStorage.getItem('token');

  useEffect(() => {
    fetchInvite(token).then(setInvite).catch(() => setError('Invitación no válida o expirada'));
  }, [token]);

  const handleAccept = async () => {
    if (!isLogged) {
      navigate(`/login?next=/invite/${token}`);
      return;
    }
    setAccepting(true);
    try {
      const res = await acceptInvite(token);
      if (res.accepted === false && res.reason === 'self') {
        toast.info('Esta es tu propia invitación 🙂');
        navigate(`/event/${invite.event_id}`);
        return;
      }
      toast.success('¡Vas con ' + invite.inviter_name + '! 🎉');
      navigate(`/event/${invite.event_id}`);
    } catch {
      toast.error('No se pudo aceptar la invitación');
    } finally {
      setAccepting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-emerald-50 to-white">
        <div className="text-center max-w-sm">
          <p className="text-6xl mb-3">🤔</p>
          <h1 className="text-2xl font-bold text-[#064E3B] mb-2">Invitación no válida</h1>
          <p className="text-gray-500 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="rounded-full bg-[#00CC66]" data-testid="invite-back-home">
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  if (!invite) return <div className="min-h-screen flex items-center justify-center text-gray-400">Cargando…</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50 flex items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md rounded-3xl border-0 shadow-2xl overflow-hidden">
        {invite.event_image && (
          <div className="h-48 overflow-hidden">
            <img src={invite.event_image} alt={invite.event_name} className="w-full h-full object-cover" />
          </div>
        )}
        <CardContent className="p-6 md:p-8 text-center">
          <div className="flex flex-col items-center -mt-12 mb-4">
            <div className="ring-4 ring-white rounded-full bg-white">
              <LevelAvatar
                name={invite.inviter_name}
                color={invite.inviter_avatar_color}
                avatarUrl={invite.inviter_avatar_url}
                level={invite.inviter_level || 1}
                size="xl"
              />
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-1">
            <span className="font-semibold text-[#064E3B]" data-testid="invite-inviter-name">{invite.inviter_name}</span> te invita a
          </p>
          <h1 className="text-2xl font-extrabold text-[#064E3B] leading-tight mb-2" data-testid="invite-event-name">
            {invite.event_name}
          </h1>
          <p className="text-sm text-gray-500 mb-6">¿Vas? Confirma para que sepa que cuenta contigo 🎉</p>

          {invite.accepted ? (
            <div className="bg-emerald-50 text-emerald-700 rounded-2xl py-4 px-5 flex items-center justify-center gap-2 font-semibold mb-3" data-testid="invite-already-accepted">
              <CheckCircle2 className="w-5 h-5" /> Ya aceptaste esta invitación
            </div>
          ) : (
            <Button
              onClick={handleAccept}
              disabled={accepting}
              className="w-full rounded-full bg-[#00CC66] hover:bg-[#00B359] py-6 text-base font-bold"
              data-testid="invite-accept-btn"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              {accepting ? 'Confirmando…' : isLogged ? '¡Voy con ' + invite.inviter_name + '!' : 'Inicia sesión para ir'}
            </Button>
          )}

          <Button
            variant="ghost"
            className="w-full mt-2 rounded-full text-emerald-700"
            onClick={() => navigate(`/event/${invite.event_id}`)}
            data-testid="invite-view-event"
          >
            Ver el evento
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="mt-4 text-gray-400 rounded-full"
            onClick={() => navigate('/')}
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> No me interesa
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvitePage;
